import {useState, useEffect, useMemo, useRef} from "react";
import {
  Card,
  BlockStack,
  Text,
  EmptyState,
  Button,
  Box,
  InlineStack,
  InlineGrid,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {PlusIcon, XIcon, PlusCircleIcon} from "@medentic/polaris-icons";
import {VisibilityIcon} from "@features/events/components/EventBadgeRenderer";
import {useTranslation} from "react-i18next";

import {Event, Brand, UUID} from "@features/events/types";
import {useQuery} from "@tanstack/react-query";
import useApiRequest from "@shared/hooks/common/useApiRequest";
import {useParams} from "react-router-dom";
import AssignBrandsModal from "@features/events/components/AssignBrandsModal";
import EventsDefaultAvatar from "@features/events/components/EventsDefaultAvatar";
import {getLocalizedText} from "@shared/lib";
import {useMyBrand} from "@features/events/hooks/useMyBrand";
import emptyEventOrganizers from "@shared/assets/empty-states/empty-event-organizers.svg";

interface OrganizersCardProps {
  organizerIds?: UUID[];
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors?: Record<string, string>;
}

interface BrandItemProps {
  brand: Brand;
  onRemove: (brandId: string) => void;
  isMyBrand: boolean;
}

const BrandItem: React.FC<BrandItemProps> = ({
  brand,
  onRemove,
  isMyBrand,
}) => {
  const {t} = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const headlineClampStyle: React.CSSProperties = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  return (
    <SortableResourceItem
      accessibilityLabel={`View details for ${getLocalizedText(brand.name) || "organizer"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedText(brand.name) || "organizer"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" blockAlign="center" wrap={false}>
            <EventsDefaultAvatar
              name={brand.name}
              singleImage={brand.logoSquare}
            />
            <Box minWidth="0" width="100%">
              <BlockStack gap="100">
                <InlineStack gap="200" blockAlign="center" wrap={false}>
                  <div style={{minWidth: 0}}>
                    <Text
                      as="span"
                      variant="bodyMd"
                      fontWeight={brand.visibility === 1 ? "medium" : "regular"}
                      tone={brand.visibility === 1 ? undefined : "subdued"}
                      truncate
                    >
                      {getLocalizedText(brand.name)}
                    </Text>
                  </div>
                  <div style={{flexShrink: 0}}>
                    <VisibilityIcon visibility={brand.visibility} />
                  </div>
                </InlineStack>
                <div style={headlineClampStyle}>
                  <Text as="span" variant="bodySm" tone="subdued">
                    {getLocalizedText(brand.headline)}
                  </Text>
                </div>
              </BlockStack>
            </Box>
          </InlineStack>

          <InlineStack
            gap="300"
            align="space-between"
            blockAlign="center"
            wrap={false}
          >
            <Box maxWidth="80%">
              <Text as="p" variant="bodyMd" tone="subdued" truncate>
                {getLocalizedText(brand.geoAddress?.summary)}
              </Text>
            </Box>
            {!isMyBrand && (
              <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
                <InlineStack gap="200" blockAlign="center" align="end">
                  <Button
                    icon={XIcon}
                    tone="critical"
                    variant="tertiary"
                    onClick={() => onRemove(brand.id)}
                    accessibilityLabel={t("eventDetails.organizers.removeOrganizer")}
                  />
                </InlineStack>
              </Box>
            )}
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

export default function OrganizersCard({
  organizerIds,
  onDataChange,
  fieldErrors,
}: OrganizersCardProps) {
  const {t} = useTranslation();
  const {eventOrganizerId: hcc} = useParams<{eventOrganizerId: string}>();
  const {GET} = useApiRequest();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const initialOrganizerIdsRef = useRef<UUID[] | undefined>(organizerIds);
  const hasAutoInitializedRef = useRef(false);

  const {myBrand} = useMyBrand(hcc);
  const myBrandId = myBrand?.id || null;

  const {data: fetchedBrands = []} = useQuery<Brand[]>({
    queryKey: ["brands", hcc],
    queryFn: () => GET(`/api/healthcare-companies/${hcc}/e/brands`),
    enabled: Boolean(hcc),
  });

  const allBrands = useMemo(() => {
    if (!myBrand) return fetchedBrands;

    const existsInFetched = fetchedBrands.some((brand) => brand.id === myBrand.id);

    if (existsInFetched) {
      return fetchedBrands.map((brand) =>
        brand.id === myBrand.id ? {...brand, isMy: true} : brand,
      );
    }

    return [{...myBrand, isMy: true}, ...fetchedBrands];
  }, [myBrand, fetchedBrands]);

  useEffect(() => {
    if (
      !hasAutoInitializedRef.current &&
      myBrandId &&
      allBrands.length > 0 &&
      initialOrganizerIdsRef.current === undefined &&
      (!organizerIds || organizerIds.length === 0)
    ) {
      onDataChange({organizerIds: [myBrandId]});
      hasAutoInitializedRef.current = true;
    }
  }, [myBrandId, allBrands, organizerIds, onDataChange]);

  const assignedOrganizers = useMemo(() => {
    if (allBrands.length === 0) return [];
    const effectiveOrganizerIds =
      organizerIds && organizerIds.length > 0
        ? organizerIds
        : myBrandId
          ? [myBrandId]
          : [];
    const orderedOrganizers = effectiveOrganizerIds
      .map((organizerId) => allBrands.find((brand) => brand.id === organizerId))
      .filter((brand): brand is Brand => brand !== undefined);

    if (myBrandId) {
      const myBrandIndex = orderedOrganizers.findIndex((b) => b.id === myBrandId);
      if (myBrandIndex > 0) {
        const myBrand = orderedOrganizers[myBrandIndex];
        orderedOrganizers.splice(myBrandIndex, 1);
        orderedOrganizers.unshift(myBrand);
      }
    }

    return orderedOrganizers;
  }, [organizerIds, allBrands, myBrandId]);

  const handleAddOrganizers = () => {
    setIsModalOpen(true);
  };

  const handleAssignOrganizers = (nextOrganizerIds: string[]) => {
    const finalIds = myBrandId
      ? [myBrandId, ...nextOrganizerIds.filter((id) => id !== myBrandId)]
      : nextOrganizerIds;

    onDataChange({organizerIds: finalIds});
    setIsModalOpen(false);
  };

  const handleRemoveOrganizer = (organizerId: string) => {
    if (organizerId === myBrandId) {
      return;
    }

    const updatedOrganizerIds = (organizerIds || []).filter(
      (id: string) => id !== organizerId,
    );
    onDataChange({organizerIds: updatedOrganizerIds});
  };

  const hasOrganizers = assignedOrganizers.length > 0;
  const hasError = fieldErrors?.organizerIds;

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="center">
            <BlockStack gap="100">
              <Text as="h2" variant="headingMd">
                {t("eventDetails.organizers.title")}
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                {t("eventDetails.organizers.description")}
              </Text>
            </BlockStack>
          </InlineStack>

          {hasOrganizers ? (
            <BlockStack gap="200">
              <Box
                borderColor={hasError ? "border-critical" : "border"}
                borderWidth="025"
                borderRadius="200"
                background="bg-surface"
              >
                <SortableList
                  items={assignedOrganizers}
                  onChange={(reorderedOrganizers) => {
                    const myBrandEntry = reorderedOrganizers.find(
                      (brand) => brand.id === myBrandId,
                    );
                    const otherBrands = reorderedOrganizers.filter(
                      (brand) => brand.id !== myBrandId,
                    );
                    const finalOrder = myBrandEntry
                      ? [myBrandEntry, ...otherBrands]
                      : reorderedOrganizers;

                    onDataChange({
                      organizerIds: finalOrder.map((brand) => brand.id),
                    });
                  }}
                  idForItem={(brand) => brand?.id}
                  isItemDragDisabled={(brand) => brand.id === myBrandId}
                  renderItem={(brand) => {
                    const isMyBrand = brand.id === myBrandId;
                    return (
                      <BrandItem
                        brand={brand}
                        onRemove={handleRemoveOrganizer}
                        isMyBrand={isMyBrand}
                      />
                    );
                  }}
                  showDividers
                />
              </Box>
              {hasError && <Text as="p" tone="critical">{hasError}</Text>}
              <InlineStack>
                <Button
                  variant="plain"
                  icon={PlusCircleIcon}
                  onClick={handleAddOrganizers}
                >
                  {t("eventDetails.organizers.addButton")}
                </Button>
              </InlineStack>
            </BlockStack>
          ) : (
            <EmptyState
              heading={t("eventDetails.organizers.emptyState")}
              action={{
                content: t("eventDetails.organizers.addButton"),
                icon: PlusIcon,
                onAction: handleAddOrganizers,
              }}
              image={emptyEventOrganizers}
            />
          )}
        </BlockStack>
      </Card>

      <AssignBrandsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedBrandIds={organizerIds || []}
        onAssign={handleAssignOrganizers}
      />
    </>
  );
}
